export type SignupGender = "female" | "male";

export interface PendingSignup {
  loginId: string;
  password: string;
  name: string;
  birthDate: string;
  gender: SignupGender;
  mobile: string;
  email: string;
}

interface StoredUser extends Omit<PendingSignup, "password"> {
  passwordHash: string;
  passwordSalt: string;
  profileImageDataUrl?: string;
  createdAt: string;
}

export interface LocalUserProfile extends Omit<StoredUser, "passwordHash" | "passwordSalt"> {}

interface EmailVerification {
  email: string;
  code: string;
  issuedAt: number;
  expiresAt: number;
}

export interface AuthSession {
  loginId: string;
  name: string;
  email: string;
  loggedInAt: string;
}

const usersKey = "art4u:local-users";
const pendingSignupKey = "art4u:pending-signup";
const emailVerificationKey = "art4u:email-verification";
const authSessionKey = "art4u:auth-session";
const emailVerificationLifetime = 3 * 60 * 1000;

function readJson<T>(storage: Storage, key: string): T | null {
  const value = storage.getItem(key);
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    storage.removeItem(key);
    return null;
  }
}

function readUsers() {
  const users = readJson<StoredUser[]>(window.localStorage, usersKey);
  return Array.isArray(users) ? users : [];
}

function bytesToHex(bytes: Uint8Array) {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function createSalt() {
  const salt = new Uint8Array(16);
  window.crypto.getRandomValues(salt);
  return bytesToHex(salt);
}

async function hashPassword(password: string, salt: string) {
  const source = new TextEncoder().encode(`${salt}:${password}`);
  const digest = await window.crypto.subtle.digest("SHA-256", source);
  return bytesToHex(new Uint8Array(digest));
}

export function isValidPassword(password: string) {
  return /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,20}$/.test(password);
}

export function formatMobile(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length === 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

export function isValidMobile(value: string) {
  return /^01\d{8,9}$/.test(value.replace(/\D/g, ""));
}

export function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export function isDuplicateLoginId(loginId: string) {
  const normalized = loginId.trim();
  return readUsers().some((user) => user.loginId === normalized);
}

export function savePendingSignup(signup: PendingSignup) {
  window.sessionStorage.setItem(pendingSignupKey, JSON.stringify(signup));
  if (!signup.email) window.sessionStorage.removeItem(emailVerificationKey);
}

export function getPendingSignup() {
  return readJson<PendingSignup>(window.sessionStorage, pendingSignupKey);
}

export function issueLocalEmailVerification(email: string) {
  const random = new Uint32Array(1);
  window.crypto.getRandomValues(random);
  const code = String(100000 + (random[0] % 900000));
  const issuedAt = Date.now();
  const verification: EmailVerification = {
    email,
    code,
    issuedAt,
    expiresAt: issuedAt + emailVerificationLifetime
  };
  window.sessionStorage.setItem(emailVerificationKey, JSON.stringify(verification));
  return verification;
}

export function getEmailVerification() {
  return readJson<EmailVerification>(window.sessionStorage, emailVerificationKey);
}

export function clearPendingSignup() {
  window.sessionStorage.removeItem(pendingSignupKey);
  window.sessionStorage.removeItem(emailVerificationKey);
}

export async function registerPendingUser(signup: PendingSignup) {
  if (isDuplicateLoginId(signup.loginId)) {
    throw new Error("이미 사용 중인 아이디입니다.");
  }
  const passwordSalt = createSalt();
  const passwordHash = await hashPassword(signup.password, passwordSalt);
  const nextUser: StoredUser = {
    loginId: signup.loginId,
    passwordHash,
    passwordSalt,
    name: signup.name,
    birthDate: signup.birthDate,
    gender: signup.gender,
    mobile: signup.mobile,
    email: signup.email,
    createdAt: new Date().toISOString()
  };
  window.localStorage.setItem(usersKey, JSON.stringify([...readUsers(), nextUser]));
  clearPendingSignup();
}

export async function authenticateLocalUser(loginId: string, password: string) {
  const user = readUsers().find((item) => item.loginId === loginId.trim());
  if (!user) return null;
  const passwordHash = await hashPassword(password, user.passwordSalt);
  if (passwordHash !== user.passwordHash) return null;

  const session: AuthSession = {
    loginId: user.loginId,
    name: user.name,
    email: user.email,
    loggedInAt: new Date().toISOString()
  };
  window.sessionStorage.setItem(authSessionKey, JSON.stringify(session));
  return session;
}

export function getAuthSession() {
  return readJson<AuthSession>(window.sessionStorage, authSessionKey);
}

function toLocalUserProfile(user: StoredUser): LocalUserProfile {
  const { passwordHash: _passwordHash, passwordSalt: _passwordSalt, ...profile } = user;
  return profile;
}

export function getCurrentLocalUser() {
  const session = getAuthSession();
  if (!session) return null;
  const user = readUsers().find((item) => item.loginId === session.loginId);
  return user ? toLocalUserProfile(user) : null;
}

export async function updateCurrentLocalUser(updates: { mobile: string; password?: string; profileImageDataUrl?: string }) {
  const session = getAuthSession();
  if (!session) throw new Error("로그인 정보가 없습니다.");

  const users = readUsers();
  const index = users.findIndex((item) => item.loginId === session.loginId);
  if (index < 0) throw new Error("회원 정보를 찾을 수 없습니다.");

  const nextUser: StoredUser = {
    ...users[index],
    mobile: formatMobile(updates.mobile)
  };
  if (updates.password) {
    nextUser.passwordSalt = createSalt();
    nextUser.passwordHash = await hashPassword(updates.password, nextUser.passwordSalt);
  }
  if (updates.profileImageDataUrl !== undefined) {
    nextUser.profileImageDataUrl = updates.profileImageDataUrl;
  }
  users[index] = nextUser;
  window.localStorage.setItem(usersKey, JSON.stringify(users));
  return toLocalUserProfile(nextUser);
}

export function clearAuthSession() {
  window.sessionStorage.removeItem(authSessionKey);
}
